import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { api, replayBase, RequestError, type ReplaySource } from "../api/client";
import { decodeTracks } from "../api/tracks";
import type { Agent } from "../api/types";
import { Controls } from "../components/Controls";
import { KillFeed } from "../components/KillFeed";
import { MapCanvas } from "../components/MapCanvas";
import { PlayerEvents } from "../components/PlayerEvents";
import { PlayerPanel } from "../components/PlayerPanel";
import { RoundStrip } from "../components/RoundStrip";
import { SignIn } from "../components/SignIn";
import { Timeline } from "../components/Timeline";
import { AbilityLookup } from "../lib/abilities";
import { useMe } from "../lib/account";
import { preload } from "../lib/images";
import { buildIndex } from "../lib/roundState";
import { OUTCOME_LABEL } from "../lib/view";
import { useDrawings } from "../player/drawings";
import { usePlayback } from "../player/store";
import { useClock } from "../player/useClock";

/** A replay opened by its owner, by id. It takes an account, and the right one. */
export function OwnedReplay() {
  const { id = "" } = useParams();
  const location = useLocation();
  const me = useMe();
  const source = useMemo<ReplaySource>(() => ({ kind: "owned", id }), [id]);

  if (me.isLoading) return <Centered>Chargement…</Centered>;
  if (!me.data?.account) {
    return (
      <div className="grid h-screen place-items-center px-6">
        <SignIn
          available={me.data?.discord ?? false}
          next={location.pathname + location.search}
          reason="Ce replay appartient à un compte. Connectez-vous pour l'ouvrir, ou demandez à son propriétaire un lien de partage."
        />
      </div>
    );
  }
  return <Viewer source={source} />;
}

/** A replay opened through its share link. No account needed, nothing changed. */
export function SharedReplay() {
  const { token = "" } = useParams();
  const source = useMemo<ReplaySource>(() => ({ kind: "shared", token }), [token]);
  return <Viewer source={source} />;
}

/** Why a replay could not be opened, in the words of the way it was opened. */
function unreadable(error: unknown, source: ReplaySource): string {
  const code = error instanceof RequestError ? error.code : "";
  switch (code) {
    case "not_found":
      return source.kind === "shared"
        ? "Ce lien de partage est inconnu."
        : "Ce replay n'existe pas, ou il appartient à un autre compte.";
    case "expired":
      return "Ce lien a expiré et les données du replay ont été supprimées.";
    case "not_ready":
      return "Ce replay est encore en cours de décodage.";
    case "unsupported_build":
    case "processing_failed":
      return error instanceof Error ? error.message : "Ce replay n'est pas lisible.";
    default:
      return "Ce replay n'est pas lisible.";
  }
}

function Viewer({ source }: { source: ReplaySource }) {
  const base = replayBase(source);
  const shared = source.kind === "shared";
  const [search, setSearch] = useSearchParams();
  useClock();

  const round = usePlayback((s) => s.round);
  const setRound = usePlayback((s) => s.setRound);

  const reference = useQuery({ queryKey: ["reference"], queryFn: api.reference, staleTime: Infinity });
  // Keyed by where the replay is read from, which is also who is reading it.
  const match = useQuery({ queryKey: ["match", base], queryFn: () => api.getMatch(base) });

  const detail = useQuery({
    queryKey: ["round", base, round],
    queryFn: () => api.getRound(base, round),
    enabled: !!match.data,
  });
  const tracks = useQuery({
    queryKey: ["tracks", base, round],
    queryFn: async () => decodeTracks(await api.getTracks(base, round)),
    enabled: !!match.data,
    staleTime: Infinity,
  });

  const rounds = match.data?.rounds ?? [];
  const current = rounds.find((r) => r.number === round);
  const map = reference.data?.maps.find((m) => m.mapUrl === match.data?.replay.mapUrl);

  const agents = useMemo(() => {
    const byCodename = new Map<string, Agent>();
    for (const agent of reference.data?.agents ?? []) byCodename.set(agent.codename, agent);
    return byCodename;
  }, [reference.data]);

  const index = useMemo(
    () => (detail.data ? buildIndex(detail.data) : new Map()),
    [detail.data],
  );

  const lookup = useMemo(() => new AbilityLookup(reference.data), [reference.data]);

  // Restore the round and instant from the URL on first load.
  useEffect(() => {
    if (!match.data || rounds.length === 0) return;
    const wanted = Number(search.get("round")) || rounds[0]!.number;
    const target = rounds.find((r) => r.number === wanted) ?? rounds[0]!;
    const offset = Number(search.get("t")) || 0;
    setRound(target.number, target.startMs, target.endMs, target.startMs + offset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.data]);

  // The notes belong to the replay, so they are fetched with it rather than
  // kept in the browser that drew them. Only the owner may change them.
  useEffect(() => {
    useDrawings.getState().load(base, source.kind === "owned" ? source.id : null);
  }, [base, source]);

  useEffect(() => {
    preload([
      map?.minimap,
      ...Array.from(agents.values()).flatMap((a) => [a.icon, ...a.abilities.map((b) => b.icon)]),
    ]);
  }, [map, agents]);

  const selectRound = (number: number) => {
    const target = rounds.find((r) => r.number === number);
    if (!target) return;
    setRound(target.number, target.startMs, target.endMs);
    setSearch({ round: String(number) }, { replace: true });
  };

  if (match.error) {
    return (
      <Centered>
        <p className="max-w-md text-center text-red-400">{unreadable(match.error, source)}</p>
        <Link to="/" className="mt-3 text-sm underline opacity-70">
          {shared ? "Aller à l'accueil" : "Retour à la bibliothèque"}
        </Link>
      </Centered>
    );
  }
  // Pending, not loading: a query waiting to retry is not fetching, but it has
  // no answer yet either, and the map cannot be known without one.
  if (match.isPending || reference.isPending) {
    return <Centered>Chargement du match…</Centered>;
  }
  if (!map) {
    return (
      <Centered>
        <p>La carte {match.data?.replay.mapUrl} n'est pas dans le référentiel.</p>
        <Link to="/" className="mt-3 text-sm underline opacity-70">
          Retour
        </Link>
      </Centered>
    );
  }

  // Only the players who actually took part in this round. In a real match
  // that is everyone; in the simulation each round belongs to one agent.
  const present = tracks.data
    ? match.data!.players.filter((p) => tracks.data!.pawns.includes(p.pawn))
    : match.data!.players;

  return (
    // The viewer is driven by dragging and clicking, not by reading, so
    // selecting its text only ever gets in the way of the playhead.
    <div className="flex h-screen select-none flex-col">
      <header className="flex items-center gap-3 border-b border-white/10 px-4 py-2">
        <Link to="/" className="text-sm opacity-60 hover:opacity-100">
          {shared ? "← Accueil" : "← Bibliothèque"}
        </Link>
        <span className="font-medium">{map.displayName}</span>
        {shared && (
          <span
            className="rounded bg-white/10 px-1.5 py-0.5 text-[11px] opacity-70"
            title="Ouvert depuis un lien de partage : les tracés sont visibles mais ne peuvent pas être modifiés."
          >
            Partagé · lecture seule
          </span>
        )}
        {current && (
          <span className="text-sm opacity-60">
            Round {current.number} · {OUTCOME_LABEL[current.outcome] ?? current.outcome}
            {current.winner ? ` · ${current.winner}` : ""}
          </span>
        )}
        <ShareLink source={source} />
      </header>

      <div className="flex min-h-0 flex-1">
        {/* The players sit on the left, where the eye starts, and everything
            that happened on the right. The map is square and never fills the
            middle, so the room it leaves goes to the two panels. */}
        <aside className="flex w-80 shrink-0 flex-col border-r border-white/10">
          {current && (
            <PlayerPanel
              players={present}
              agents={agents}
              weapons={reference.data?.weapons ?? []}
              lookup={lookup}
              round={current}
              index={index}
              effects={detail.data?.effects ?? []}
            />
          )}
        </aside>

        <section className="flex min-w-0 flex-1 flex-col">
          <RoundStrip rounds={rounds} current={round} onSelect={selectRound} />
          <div className="min-h-0 flex-1 p-3">
            {current && detail.data && (
              <MapCanvas
                map={map}
                players={match.data!.players}
                agents={agents}
                lookup={lookup}
                round={current}
                detail={detail.data}
                tracks={tracks.data}
              />
            )}
          </div>
          {current && detail.data && (
            <div className="border-t border-white/10">
              <Timeline
                round={current}
                events={detail.data.events}
                players={match.data!.players}
              />
              <Controls
                round={current}
                hasPrevious={round > rounds[0]!.number}
                hasNext={round < rounds[rounds.length - 1]!.number}
                onPreviousRound={() => selectRound(round - 1)}
                onNextRound={() => selectRound(round + 1)}
              />
            </div>
          )}
        </section>

        <aside className="flex w-80 shrink-0 flex-col border-l border-white/10">
          {current && detail.data && (
            <div className="max-h-[45%] shrink-0 overflow-y-auto border-b border-white/10">
              <KillFeed
                events={detail.data.events}
                players={match.data!.players}
                agents={agents}
                round={current}
                map={map}
              />
            </div>
          )}
          {current && detail.data && (
            <PlayerEvents
              players={match.data!.players}
              agents={agents}
              lookup={lookup}
              round={current}
              detail={detail.data}
            />
          )}
        </aside>
      </div>
    </div>
  );
}

/**
 * Copies a share link to the current round and instant.
 *
 * The owner's own address only works for the owner, so what is copied is
 * always the share link, made on the first copy. Somebody who came in through
 * one copies the same link, pointing at where they are.
 */
function ShareLink({ source }: { source: ReplaySource }) {
  const round = usePlayback((s) => s.round);
  const startMs = usePlayback((s) => s.startMs);
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
  // The link itself, shown to copy by hand when the clipboard says no: it
  // does on any page not served over HTTPS or from localhost.
  const [manual, setManual] = useState<string | null>(null);
  const timer = useRef<number>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const show = (next: "copied" | "failed") => {
    setState(next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setState("idle"), 2500);
  };

  const copy = async () => {
    const t = Math.round(usePlayback.getState().timeMs - startMs);
    const link = (token: string) =>
      `${window.location.origin}/r/${encodeURIComponent(token)}?round=${round}&t=${t}`;
    const url =
      source.kind === "shared"
        ? Promise.resolve(link(source.token))
        : api.share(source.id).then(link);
    setManual(null);
    try {
      // The clipboard is written while the click still counts as one: a
      // browser may refuse a write that comes after waiting on the server,
      // so the write is handed the pending text rather than made after it.
      if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
        const blob = url.then((text) => new Blob([text], { type: "text/plain" }));
        await navigator.clipboard.write([new ClipboardItem({ "text/plain": blob })]);
      } else {
        await navigator.clipboard.writeText(await url);
      }
      show("copied");
    } catch {
      // The link may well exist even though the clipboard refused it.
      const text = await url.catch(() => null);
      if (text) setManual(text);
      else show("failed");
    }
  };

  return (
    <div className="relative ml-auto">
      <button
        onClick={copy}
        className="rounded px-2 py-1 text-xs opacity-60 hover:bg-white/10 hover:opacity-100"
        title="Copier un lien de partage vers cet instant. Il ouvre le replay sans compte, en lecture seule."
      >
        {state === "copied"
          ? "Lien copié"
          : state === "failed"
            ? "Lien indisponible"
            : "Copier le lien de partage"}
      </button>
      {manual && (
        <div className="absolute right-0 z-30 mt-1 w-96 rounded-lg border border-white/10 bg-neutral-900 p-2 text-xs shadow-xl">
          <p className="mb-1.5 opacity-70">
            Le navigateur refuse l'accès au presse-papiers. Copiez le lien :
          </p>
          <div className="flex gap-1">
            <input
              readOnly
              value={manual}
              autoFocus
              onFocus={(e) => e.currentTarget.select()}
              className="min-w-0 flex-1 rounded bg-white/10 px-2 py-1 font-mono text-[11px] select-all"
            />
            <button
              onClick={() => setManual(null)}
              className="rounded px-2 hover:bg-white/10"
              title="Fermer"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid h-screen place-items-center">
      <div className="flex flex-col items-center text-sm opacity-80">{children}</div>
    </div>
  );
}
